import {useCallback, useEffect, useMemo, useState} from "react"
import axios from "axios"
import {Alert, Badge, Button, Card, Form, Spinner, Table} from "react-bootstrap"
import moment from "moment"

import {useAuth} from "../../context/AuthProvider"
import {useTranslation} from "react-i18next"

type EvaluationConfig = {
    url: string
    enabled: boolean
    sentTo: string[]
}

type EvaluationExam = {
    _id: string
    name_de: string
    name_en: string
    closeExaminationRange: string
    evaluation?: EvaluationConfig
}

type Draft = {url: string; enabled: boolean}
type SendResult = {eligible: number; queued: number; skipped: number; failed: number}

export default function EvaluationManagementComponent() {
    const {t, i18n} = useTranslation("admin")
    const auth = useAuth()
    const isAdmin = auth.hasGate("admin")
    const [exams, setExams] = useState<EvaluationExam[]>([])
    const [drafts, setDrafts] = useState<Record<string, Draft>>({})
    const [query, setQuery] = useState("")
    const [loading, setLoading] = useState(true)
    const [busy, setBusy] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<string | null>(null)

    const load = useCallback(async () => {
        if (!isAdmin) return setLoading(false)
        setLoading(true)
        try {
            const response = await axios.get<EvaluationExam[]>(
                "/api/evaluation",
                auth.axiosAuthHeader()
            )
            setExams(response.data)
            setDrafts(Object.fromEntries(response.data.map(exam => [exam._id, {
                url: exam.evaluation?.url ?? "",
                enabled: exam.evaluation?.enabled === true,
            }])))
            setError(null)
        } catch {
            setError(t("evaluations.messages.loadError"))
        } finally {
            setLoading(false)
        }
    }, [auth, isAdmin, t])

    useEffect(() => { load() }, [load])

    const filteredExams = useMemo(() => {
        const term = query.trim().toLocaleLowerCase()
        return term ? exams.filter(exam =>
            [exam.name_de, exam.name_en].some(name =>
                name?.toLocaleLowerCase().includes(term)
            )
        ) : exams
    }, [exams, query])

    const save = async (exam: EvaluationExam) => {
        setBusy(`save:${exam._id}`)
        setError(null)
        setSuccess(null)
        try {
            const response = await axios.put<EvaluationExam>(
                `/api/evaluation/${exam._id}`,
                drafts[exam._id],
                auth.axiosAuthHeader()
            )
            setExams(previous => previous.map(item =>
                item._id === exam._id ? response.data : item
            ))
            setSuccess(t("evaluations.messages.saved", {exam: exam.name_de}))
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.message ?? t("evaluations.messages.saveError")
                : t("evaluations.messages.saveError"))
        } finally {
            setBusy(null)
        }
    }

    const send = async (exam: EvaluationExam) => {
        if (!window.confirm(t("evaluations.confirmSend", {exam: exam.name_de}))) return
        setBusy(`send:${exam._id}`)
        setError(null)
        setSuccess(null)
        try {
            const response = await axios.post<SendResult>(
                `/api/evaluation/${exam._id}/send`,
                {},
                auth.axiosAuthHeader()
            )
            setSuccess(t("evaluations.messages.sent", response.data))
            await load()
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.message ?? t("evaluations.messages.sendError")
                : t("evaluations.messages.sendError"))
        } finally {
            setBusy(null)
        }
    }

    if (!isAdmin) return <div className="component"><Alert variant="danger">
        {t("adminOnly")}
    </Alert></div>

    return <div className="component">
        <Card>
            <Card.Header><h2 className="mb-0"><i className="bi bi-chat-square-text me-2"/>
                {t("evaluations.title")}</h2></Card.Header>
            <Card.Body>
                <p>{t("evaluations.description")}</p>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}
                <Form.Control
                    className="mb-3"
                    type="search"
                    value={query}
                    placeholder={t("evaluations.search")}
                    onChange={event => setQuery(event.target.value)}
                />
                {loading ? <div className="text-center"><Spinner/></div> : (
                    <Table responsive hover className="align-middle">
                        <thead><tr>
                            <th>{t("evaluations.exam")}</th>
                            <th>{t("evaluations.url")}</th>
                            <th>{t("evaluations.status")}</th>
                            <th>{t("evaluations.sent")}</th>
                            <th>{t("evaluations.actions")}</th>
                        </tr></thead>
                        <tbody>{filteredExams.map(exam => {
                            const draft = drafts[exam._id] ?? {url: "", enabled: false}
                            const isSaving = busy === `save:${exam._id}`
                            const isSending = busy === `send:${exam._id}`
                            return <tr key={exam._id}>
                                <td><strong>{i18n.language === "en"
                                    ? exam.name_en || exam.name_de
                                    : exam.name_de}</strong><br/>
                                    <small className="text-muted">{moment(exam.closeExaminationRange).format("LL")}</small>
                                </td>
                                <td><Form.Control type="url" value={draft.url}
                                    placeholder="https://…"
                                    onChange={event => setDrafts(previous => ({
                                        ...previous,
                                        [exam._id]: {...draft, url: event.target.value},
                                    }))}/></td>
                                <td><Form.Check type="switch"
                                    label={draft.enabled ? t("evaluations.enabled") : t("evaluations.disabled")}
                                    checked={draft.enabled}
                                    onChange={event => setDrafts(previous => ({
                                        ...previous,
                                        [exam._id]: {...draft, enabled: event.target.checked},
                                    }))}/></td>
                                <td><Badge bg="secondary">{exam.evaluation?.sentTo?.length ?? 0}</Badge></td>
                                <td className="text-nowrap">
                                    <Button size="sm" className="me-2" disabled={busy !== null}
                                        onClick={() => save(exam)}>
                                        {isSaving ? <Spinner size="sm"/> : t("evaluations.save")}
                                    </Button>
                                    <Button size="sm" variant="success"
                                        disabled={busy !== null || !exam.evaluation?.enabled || !exam.evaluation?.url}
                                        onClick={() => send(exam)}>
                                        {isSending ? <Spinner size="sm"/> : t("evaluations.send")}
                                    </Button>
                                </td>
                            </tr>
                        })}</tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    </div>
}
